/**
 * OverviewV2 — Project Overview redesign (built alongside the legacy
 * `features/projects/ProjectOverview.jsx` placeholder, à la the v2
 * workflow tab pattern from Epic 12).
 *
 * Composes the 13 cards from `components/` into a 12-column grid.
 * Cards receive their data via the `data` prop (no per-card
 * fetching) so this shell owns the data layer (`hooks/useOverviewData`)
 * and aggregation (`aggregate/aggregateOverview`).
 *
 * Mounted under `?tab=overview-v2` in
 * `features/projects/ProjectWorkspace.jsx`. Will swap into the
 * canonical `overview` tab when v2 is approved (separate small commit).
 */
import React from 'react';
import { useOverviewData } from './hooks/useOverviewData';
import StatusBanner from './components/StatusBanner/StatusBanner';
import RiskTreatmentMatrixCard from './components/RiskTreatmentMatrixCard/RiskTreatmentMatrixCard';
import StrideCard from './components/StrideCard/StrideCard';
import RiskScoreCard from './components/RiskScoreCard/RiskScoreCard';
import CoverageChainCard from './components/CoverageChainCard/CoverageChainCard';
import AttackPathsCard from './components/AttackPathsCard/AttackPathsCard';
import OrphansCard from './components/OrphansCard/OrphansCard';
import InheritanceCard, {
  shouldRender as shouldRenderInheritance,
} from './components/InheritanceCard/InheritanceCard';
import OverviewSkeleton from './components/OverviewSkeleton';
import styles from './OverviewV2.module.css';

interface OverviewV2Props {
  project?: {
    thread_id?: string;
    session_id?: string;
    analysis_mode?: 'individual' | 'hierarchical' | null;
  };
  threadId?: string;
  sessionId?: string;
  /** True while the workspace's getSession fetch is still in flight
   *  (provided via the outlet context). MUST be checked before the
   *  no-threadId branch, because threadId===undefined is ambiguous
   *  between "project still loading" and "project has no workflow". */
  projectLoading?: boolean;
}

export default function OverviewV2({ project, threadId, sessionId, projectLoading = false }: OverviewV2Props) {
  const resolvedThreadId = threadId ?? project?.thread_id ?? null;
  const resolvedSessionId = sessionId ?? project?.session_id ?? null;
  const analysisMode = project?.analysis_mode ?? null;

  const { data, loading, error } = useOverviewData({
    threadId: resolvedThreadId,
    sessionId: resolvedSessionId,
    analysisMode,
  });

  return (
    <div className={styles.container}>
      {projectLoading && (
        // Project document still loading — show the grid skeleton, not
        // the no-analysis placeholder (threadId isn't knowable yet).
        <OverviewSkeleton />
      )}

      {!projectLoading && !resolvedThreadId && (
        <div className={styles.placeholder}>
          <p>No analysis available yet. Run the TARA Workflow first.</p>
        </div>
      )}

      {!projectLoading && resolvedThreadId && loading && !data && (
        // Same grid skeleton as above — the page keeps one continuous
        // loading layout from getSession through the aggregate fetch.
        <OverviewSkeleton />
      )}

      {error && (
        <div className={styles.placeholder}>
          <p>Failed to load overview: {error}</p>
        </div>
      )}

      {data && (
        <div className={styles.grid}>
          {/* Row 0 — full-width status banner. */}
          <div className={styles.spanFull}>
            <StatusBanner data={data} />
          </div>

          {/* Row 1 — risk headline: gauge (3) + the 4×4 severity ×
              treatment heat map (9). One-glance risk story: score +
              where every risk sits and how it is being treated. */}
          <div className={styles.span3}>
            <RiskScoreCard data={data} />
          </div>
          <div className={styles.span9}>
            <RiskTreatmentMatrixCard data={data} />
          </div>

          {/* Row 2 — STRIDE pie (6) + top risk attack paths (6). */}
          <div className={styles.span6}>
            <StrideCard data={data} />
          </div>
          <div className={styles.span6}>
            <AttackPathsCard data={data} />
          </div>

          {/* Row 3 — traceability coverage (6) + orphan audit findings (6).
              Paired: each coverage gap IS an orphan finding. */}
          <div className={styles.span6}>
            <CoverageChainCard data={data} />
          </div>
          <div className={styles.span6}>
            <OrphansCard data={data} />
          </div>

          {/* Row 4 — inheritance (hierarchical only). */}
          {shouldRenderInheritance(data) && (
            <div className={styles.span6}>
              <InheritanceCard data={data} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
